import { supabase } from './supabaseClient'
import { getBrowserId, getVisitorName, getStoredUtm, captureUtmParams } from './lib/visitorContext'

const ACTIVE_START_KEY = '_portfolio_active_start'
let locationDetails = {}
let locationPromise = null
let locationReady = false

const getLocationDetails = () => {
  if (!locationPromise) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 2000)
    locationPromise = fetch('/api/visitor-location', {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((response) => response.ok ? response.json() : null)
      .then((location) => {
        if (!location || typeof location !== 'object') return {}
        locationDetails = {
          country: location.country || null,
          country_code: location.country_code || null,
          region: location.region || null,
          city: location.city || null,
          ip_timezone: location.ip_timezone || null,
        }
        return locationDetails
      })
      .catch(() => ({}))
      .finally(() => {
        clearTimeout(timeout)
        locationReady = true
      })
  }
  return locationPromise
}

const waitBrieflyForLocation = () => locationReady
  ? Promise.resolve()
  : Promise.race([
      getLocationDetails(),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ])

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem('_portfolio_sid')
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      sessionStorage.setItem('_portfolio_sid', sid)
    }
    return sid
  } catch {
    return 'unknown'
  }
}

const ensureActiveStart = () => {
  try {
    if (!sessionStorage.getItem(ACTIVE_START_KEY)) {
      sessionStorage.setItem(ACTIVE_START_KEY, String(Date.now()))
    }
  } catch {
    /* ignore */
  }
}

const takeActiveDurationMs = () => {
  try {
    const start = Number(sessionStorage.getItem(ACTIVE_START_KEY) || 0)
    sessionStorage.removeItem(ACTIVE_START_KEY)
    if (!start) return 0
    return Math.max(0, Date.now() - start)
  } catch {
    return 0
  }
}

const parseSource = (referrer, utmSource) => {
  if (utmSource) return utmSource.toLowerCase()
  if (!referrer) return 'direct'
  try {
    const host = new URL(referrer).hostname.toLowerCase()
    if (host.includes('twitter') || host.includes('t.co') || host.includes('x.com')) return 'twitter'
    if (host.includes('linkedin')) return 'linkedin'
    if (host.includes('github')) return 'github'
    if (host.includes('google')) return 'google'
    if (host.includes('facebook') || host.includes('fb.')) return 'facebook'
    if (host.includes('instagram')) return 'instagram'
    if (host.includes('reddit')) return 'reddit'
    if (host.includes('youtube')) return 'youtube'
    if (host.includes('whatsapp')) return 'whatsapp'
    if (host.includes('telegram')) return 'telegram'
    return host.replace('www.', '')
  } catch {
    return 'unknown'
  }
}

const isLocalEnvironment = () => {
  if (process.env.REACT_APP_TRACK_LOCAL === 'true') return false
  const host = window.location.hostname
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    host.endsWith('.local')
  )
}

const isExcludedPath = (page) => {
  const path = String(page || window.location.pathname)
  return path.startsWith('/dashboard')
}

const getDevice = () => {
  try {
    const mobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
    return mobile ? 'mobile' : 'desktop'
  } catch {
    return 'unknown'
  }
}

const getLanguage = () => {
  try {
    return navigator.language || null
  } catch {
    return null
  }
}

const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

const getRefParam = () => {
  try {
    return new URLSearchParams(window.location.search).get('ref') || null
  } catch {
    return null
  }
}

const isFounderRoute = () => {
  try {
    return window.location.pathname.startsWith('/hi/')
  } catch {
    return false
  }
}

const buildMetadata = (extra) => {
  const metadata = {
    timezone: getTimezone(),
    ...locationDetails,
  }
  if (extra && typeof extra === 'object' && !Array.isArray(extra)) {
    Object.assign(metadata, extra)
  }
  return metadata
}

const buildEventPayload = (
  event_type,
  {
    page,
    target,
    click_location,
    company,
    project_title,
    entry_route,
    duration_ms,
    metadata,
  } = {}
) => {
  captureUtmParams()
  const { utm_source, utm_medium, utm_campaign } = getStoredUtm()
  const referrer = document.referrer || null
  const source = parseSource(referrer, utm_source)
  const resolvedPage = page || window.location.pathname

  const event = {
    event_type,
    page: resolvedPage,
    target: target || null,
    source,
    referrer,
    utm_source,
    utm_medium,
    utm_campaign,
    user_agent: navigator.userAgent,
    session_id: getSessionId(),
    visitor_id: getBrowserId(),
    visitor_name: getVisitorName(),
    device: getDevice(),
    language: getLanguage(),
    is_founder_route: isFounderRoute(),
    ref_param: getRefParam(),
    click_location: click_location || null,
    company: company || null,
    project_title: project_title || null,
    entry_route: entry_route || null,
    metadata: buildMetadata(metadata),
    seen: false,
  }

  if (typeof duration_ms === 'number') {
    event.duration_ms = duration_ms
  }

  return event
}

const insertViaKeepalive = (event) => {
  const url = process.env.REACT_APP_SUPABASE_URL
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY
  if (!url || !key) return false

  try {
    fetch(`${url}/rest/v1/analytics_events`, {
      method: 'POST',
      headers: {
        apikey: key,
        Authorization: `Bearer ${key}`,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body: JSON.stringify(event),
      keepalive: true,
    }).catch(() => {})
    return true
  } catch {
    return false
  }
}

let lastPageView = { page: '', at: 0 }
let sessionCloseBound = false

export const trackEvent = async (
  event_type,
  {
    page,
    target,
    click_location,
    company,
    project_title,
    entry_route,
    duration_ms,
    metadata,
  } = {}
) => {
  if (!supabase || isLocalEnvironment()) return

  const resolvedPage = page || window.location.pathname
  if (isExcludedPath(resolvedPage)) return

  ensureActiveStart()

  const event = buildEventPayload(event_type, {
    page: resolvedPage,
    target,
    click_location,
    company,
    project_title,
    entry_route,
    duration_ms,
    metadata,
  })

  await waitBrieflyForLocation()
  event.metadata = { ...event.metadata, ...locationDetails }

  const { error } = await supabase.from('analytics_events').insert(event)
  if (error) console.warn('[tracker]', error.message)
}

export const logPageView = (page) => {
  const resolved = String(page)
  const now = Date.now()
  if (lastPageView.page === resolved && now - lastPageView.at < 2500) return
  lastPageView = { page: resolved, at: now }
  return trackEvent('page_view', {
    page: resolved,
    metadata: { timezone: getTimezone() },
  })
}

export const logResumeClick = (click_location = 'hero') =>
  trackEvent('resume_click', {
    target: 'resume_download',
    click_location,
  })

export const logBlogOpen = (slug) =>
  trackEvent('blog_open', { target: String(slug) })

export const logBlogScrollDepth = (slug, scrollDepth) =>
  trackEvent('blog_scroll_depth', {
    target: String(slug),
    click_location: `scroll_${scrollDepth}`,
    metadata: { scroll_depth: scrollDepth },
  })

export const logBlogReadTime = (slug, duration_ms, scrollDepth, { keepalive = false } = {}) => {
  if (typeof duration_ms !== 'number' || duration_ms < 1000) return

  const payload = {
    page: window.location.pathname,
    target: String(slug),
    click_location: keepalive ? 'unload' : 'leave',
    duration_ms,
    metadata: {
      scroll_depth: typeof scrollDepth === 'number' ? scrollDepth : 0,
    },
  }

  if (keepalive) {
    if (!supabase || isLocalEnvironment()) return
    if (isExcludedPath(window.location.pathname)) return
    insertViaKeepalive(buildEventPayload('blog_read_time', payload))
    return
  }

  return trackEvent('blog_read_time', payload)
}

export const logProjectOpen = (slug) =>
  trackEvent('project_open', { target: String(slug) })

export const logContactClick = (type, click_location = 'contact') =>
  trackEvent('contact_click', {
    target: String(type),
    click_location,
  })

export const logSocialClick = (platform, click_location = 'hero') =>
  trackEvent('social_click', {
    target: String(platform),
    click_location,
  })

export const logWhyHireMeOpen = () =>
  trackEvent('why_hire_me_open', { target: 'modal', click_location: 'hero' })

export const logVisitorIdentified = (visitorSlug) =>
  trackEvent('visitor_identified', {
    target: String(visitorSlug),
    entry_route: window.location.pathname,
  })

export const logExperienceTabClick = (tabId, company) =>
  trackEvent('experience_tab_click', {
    target: String(tabId),
    company: String(company || ''),
  })

export const logFeaturedProjectModalOpen = (projectSlug, projectTitle) =>
  trackEvent('featured_project_modal_open', {
    target: String(projectSlug),
    project_title: String(projectTitle || ''),
  })

let lastCloseAt = 0

export const logSessionClose = () => {
  if (!supabase || isLocalEnvironment()) return
  if (isExcludedPath(window.location.pathname)) return

  const now = Date.now()
  if (now - lastCloseAt < 800) return
  lastCloseAt = now

  const duration_ms = takeActiveDurationMs()
  if (duration_ms < 1000) return

  const event = buildEventPayload('session_close', {
    page: window.location.pathname,
    target: 'portfolio_close',
    click_location: 'unload',
    duration_ms,
  })

  insertViaKeepalive(event)
}

export const initSessionTracking = () => {
  if (sessionCloseBound || typeof window === 'undefined') return
  sessionCloseBound = true

  ensureActiveStart()
  if (supabase && !isLocalEnvironment() && !isExcludedPath(window.location.pathname)) {
    getLocationDetails()
  }

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      logSessionClose()
    } else {
      ensureActiveStart()
    }
  }

  window.addEventListener('pagehide', logSessionClose)
  document.addEventListener('visibilitychange', onVisibility)
}
